import { Box, Button, Checkbox, Group, Text } from "@mantine/core";
import { CellEditRequestEvent, ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo } from "react";
import { AG_GRID_LOCALE_NOR } from "../../../../../agGridLocale";
import { ISJArapportOption } from "@mcapps/shared/reportSchemas/sjaSchema";

export const SJAOptionsTable: React.FC<{
  category: string;
  editData: ISJArapportOption;
  updateData: (key: string, value: any) => void;
}> = ({ category, editData, updateData }) => {
  const rowData = useMemo(
    () => editData?.faremomenter?.map((item, i) => ({ ...item, _id: i })),
    [editData, editData?.isSelected],
  );
  const updateRowCheckbox = (index: number, value: boolean) => {
    const dataToUpdate = { ...editData };
    dataToUpdate.faremomenter = [...editData.faremomenter];
    dataToUpdate.faremomenter[index].isSelected = value;

    dataToUpdate.isSelected = dataToUpdate.faremomenter.some((f) => f.isSelected);

    updateData(`options.${category}`, dataToUpdate);
  };

  const updateAllOptionsInCategory = (category: string, value: boolean) => {
    let dataToUpdate = editData;
    dataToUpdate.isSelected = value;
    dataToUpdate?.faremomenter?.forEach((_, index) => {
      dataToUpdate.faremomenter[index].isSelected = value;
    });
    updateData(`options.${category}`, dataToUpdate);
  };

  const onCellEditRequest = (event: CellEditRequestEvent<any>) => {
    let faremomenter = [...editData?.faremomenter];
    faremomenter[event.node.rowIndex][event.colDef.field] = event.newValue;

    updateData(`options.${category}.faremomenter`, faremomenter);
  };

  const addNewRow = () => {
    const newRow = {
      isSelected: true,
      deloppgave: "",
      faremoment: "",
      tiltak: "",
      restrisiko: "",
      ansvarlig: "",
      kommentar: "",
    };

    const updatedFaremomenter = [...editData.faremomenter, newRow];

    updateData(`options.${category}.faremomenter`, updatedFaremomenter);
  };

  const columns: ColDef<any>[] = useMemo(
    () => [
      {
        headerName: "",
        field: "isSelected",
        cellRenderer: (params) => {
          return (
            <Checkbox
              checked={params?.value}
              onChange={() => updateRowCheckbox(params.node.rowIndex, !params?.value)}
            />
          );
        },
        suppressHeaderMenuButton: true,
        width: 40,
        cellClass: "px-1",
      },
      {
        headerName: "Deloppgave",
        field: "deloppgave",
        suppressHeaderMenuButton: true,
        flex: 1,
        cellClass: "d-block",
        editable: true,
      },
      {
        headerName: "Faremoment",
        field: "faremoment",
        suppressHeaderMenuButton: true,
        flex: 1,
        cellClass: "d-block",
        editable: true,
      },
      {
        headerName: "Forslag til tiltak",
        field: "tiltak",
        suppressHeaderMenuButton: true,
        flex: 1.5,
        cellClass: "d-block",
        editable: true,
      },
      {
        headerName: "Restrisiko",
        field: "restrisiko",
        suppressHeaderMenuButton: true,
        editable: true,
        flex: 1,
      },
      {
        headerName: "Ansvarlig",
        field: "ansvarlig",
        suppressHeaderMenuButton: true,
        editable: true,
        width: 150,
      },
      {
        headerName: "Kommentar",
        field: "kommentar",
        suppressHeaderMenuButton: true,
        editable: true,
        flex: 1,
      },
    ],
    [rowData?.length],
  );

  return (
    <Box className="ag-theme-quartz" w="100%">
      <Group mb="sm">
        <Checkbox
          checked={editData?.isSelected}
          onChange={() => updateAllOptionsInCategory(`${category}`, !editData?.isSelected)}
        />
        <Text fw={600} fz="md">
          {editData?.name}
        </Text>
      </Group>
      <Button size="sm" mb="xs" onClick={addNewRow}>
        Legg til rad
      </Button>
      <AgGridReact
        rowHeight={40}
        domLayout="autoHeight"
        columnDefs={columns}
        getRowId={(p) => p.data._id}
        rowData={rowData}
        stopEditingWhenCellsLoseFocus={true}
        animateRows={false}
        readOnlyEdit={true}
        suppressContextMenu={true}
        maintainColumnOrder={true}
        onCellEditRequest={onCellEditRequest}
        localeText={AG_GRID_LOCALE_NOR}
      />
    </Box>
  );
};
